import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import SearchableSelect from '../ui/SearchableSelect';
import { postScheduledResource, fetchTicketsByProjectIds, getTicketSummary, postAddMemberToProject, fetchProjectRoles } from '../../api/client';
import { buildApiUrl, apiConfig } from '../../apiConfig';

// Reuses the same postScheduledResource API action as the main scheduler modal.

function toUtcIso(dateStr) {
  if (!dateStr) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr));
  if (!m) return null;
  const utc = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0));
  return utc.toISOString().slice(0, 19) + 'Z';
}

const S = {
  row: { marginBottom: 14 },
  label: { display: 'block', fontWeight: 600, fontSize: 12, color: '#b0bec5', marginBottom: 5 },
  input: {
    width: '100%',
    padding: '8px 10px',
    background: '#37474f',
    border: '1px solid #546e7a',
    borderRadius: 6,
    color: '#eceff1',
    fontSize: 13,
    boxSizing: 'border-box',
  },
  row2: { display: 'flex', gap: 8 },
  btn: {
    padding: '7px 14px',
    background: '#37474f',
    border: '1px solid #546e7a',
    borderRadius: 6,
    color: '#eceff1',
    cursor: 'pointer',
    fontSize: 13,
  },
  primaryBtn: {
    padding: '9px 18px',
    background: '#1565c0',
    border: '2px solid #0d47a1',
    borderRadius: 6,
    color: '#fff',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
  },
  error: { color: '#ef5350', fontSize: 12, marginTop: 6 },
};

export default function UtilizationAddModal({ open, onClose, onSuccess, token, loadedTickets = [], presetTicket = null }) {
  const [projectInput, setProjectInput] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [projectTickets, setProjectTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketError, setTicketError] = useState(null);

  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [hours, setHours] = useState('');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dateStart, setDateStart] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showAddToProject, setShowAddToProject] = useState(false);
  const [projectRoles, setProjectRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [addToProjectRoleId, setAddToProjectRoleId] = useState(null);
  const [addingToProject, setAddingToProject] = useState(false);
  const [addToProjectError, setAddToProjectError] = useState(null);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setProjectInput('');
    setTicketError(null);
    setSelectedProjectId(presetTicket ? Number(presetTicket.projectId) : null);
    setProjectTickets(presetTicket
      ? [{ id: presetTicket.ticketId, summary: presetTicket.ticketSummary, project: { name: presetTicket.projectName, id: presetTicket.projectId } }]
      : []);
    setSelectedTicketId(presetTicket ? String(presetTicket.ticketId) : null);
    setHours('');
    setSelectedUser(null);
    setSubmitError(null);
    setSuccess(false);
    setShowAddToProject(false);
    setAddToProjectRoleId(null);
    setAddToProjectError(null);
    const today = new Date();
    const pad = n => String(n).padStart(2, '0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    setDateStart(fmt(today));
  }, [open, presetTicket]);

  // Load all active users once
  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingUsers(true);
        const url = buildApiUrl(apiConfig.cwUsersPath, { conditions: 'inactiveFlag=false' });
        const res = await fetch(url, { headers: { Accept: 'application/json', Authorization: token } });
        const data = res.ok ? await res.json() : [];
        const list = Array.isArray(data) ? data : (data?.items || []);
        if (!cancelled) setUsers(list);
      } catch (e) {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, token]);

  async function handleProjectSearch() {
    const raw = projectInput.trim();
    if (!raw) return;
    const numId = /^\d+$/.test(raw) ? Number(raw) : null;
    if (!numId) {
      setTicketError('Enter a numeric project ID.');
      return;
    }
    setTicketError(null);
    setSelectedProjectId(numId);
    setSelectedTicketId(null);
    setProjectTickets([]);

    // First look in already-loaded tickets (fast path)
    const fromLoaded = loadedTickets.filter(t => {
      const pid = t?.project?.id ?? t?.projectId;
      return pid != null && String(pid) === String(numId);
    });
    if (fromLoaded.length > 0) {
      setProjectTickets(fromLoaded);
      return;
    }

    // Otherwise fetch from API
    try {
      setLoadingTickets(true);
      const list = await fetchTicketsByProjectIds([numId], token, { pageSize: 100 });
      if (!cancelled) setProjectTickets(Array.isArray(list) ? list : []);
    } catch (e) {
      setTicketError(`Failed to load tickets: ${e.message}`);
    } finally {
      setLoadingTickets(false);
    }
    var cancelled = false; // eslint-disable-line no-var
  }

  const ticketOptions = useMemo(() =>
    projectTickets.map(t => {
      const id = t?.id ?? t?.ticketId;
      const summary = getTicketSummary(t) || `Ticket ${id}`;
      return { value: String(id), label: `#${id} – ${summary}` };
    }),
    [projectTickets]
  );

  const userOptions = useMemo(() =>
    users.map(u => ({
      value: String(u?.id ?? u?.identifier ?? ''),
      label: u?.name || u?.fullName || u?.identifier || String(u?.id ?? ''),
      raw: u,
    })),
    [users]
  );

  async function handleSubmit() {
    try {
      setSubmitError(null);
      if (!selectedProjectId) throw new Error('Enter and search a project ID first.');
      if (!selectedTicketId) throw new Error('Select a ticket.');
      if (!selectedUser) throw new Error('Select a resource.');
      if (!dateStart) throw new Error('Enter a start date.');
      const parsedHours = hours !== '' ? Number(hours) : 0;
      if (Number.isNaN(parsedHours) || parsedHours < 0) throw new Error('Hours must be a positive number.');
      const ds = toUtcIso(dateStart);
      if (!ds) throw new Error('Invalid date format (YYYY-MM-DD required).');
      // dateEnd = dateStart + hours as actual hours offset
      const endD = new Date(dateStart + 'T00:00:00Z');
      endD.setUTCHours(endD.getUTCHours() + Math.floor(parsedHours));
      endD.setUTCMinutes(endD.getUTCMinutes() + Math.round((parsedHours % 1) * 60));
      const de = endD.toISOString().slice(0, 19) + 'Z';

      const member = selectedUser.raw?.id != null
        ? { id: selectedUser.raw.id }
        : { identifier: String(selectedUser.raw?.identifier ?? '') };

      const payload = {
        objectId: Number(selectedTicketId),
        member,
        dateStart: ds,
        dateEnd: de,
        type: { identifier: 'S' },
        span: { identifier: 'N' },
      };

      setSubmitting(true);
      await postScheduledResource(payload, token);

      const selectedTicket = projectTickets.find(
        t => String(t?.id ?? t?.ticketId) === selectedTicketId
      );
      onSuccess?.({
        ticketId: selectedTicketId,
        ticketNumber: selectedTicketId,
        ticketSummary: getTicketSummary(selectedTicket) || `Ticket ${selectedTicketId}`,
        projectName: selectedTicket?.project?.name || '',
        projectId: String(selectedProjectId ?? ''),
        memberName: selectedUser.label,
        memberIdentifier: String(selectedUser.raw?.identifier ?? selectedUser.raw?.id ?? ''),
        scheduledHours: parsedHours || 0,
        dateStart,
      });
      setSuccess(true);
    } catch (e) {
      const msg = e?.message || String(e);
      const isNotAssigned = msg.toLowerCase().includes('member not assigned to project');
      setSubmitError(isNotAssigned ? 'Member is not assigned to this project.' : msg);
      if (isNotAssigned && selectedProjectId && selectedUser) {
        setShowAddToProject(true);
        setLoadingRoles(true);
        try {
          const roles = await fetchProjectRoles(token);
          const roleList = Array.isArray(roles) ? roles : (roles?.items || []);
          setProjectRoles(roleList);
          const first = roleList.find(r => !r?.inactiveFlag) || roleList[0];
          setAddToProjectRoleId(first?.id ?? null);
        } catch {
          setProjectRoles([]);
        } finally {
          setLoadingRoles(false);
        }
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAddToProject() {
    if (!selectedProjectId || !selectedUser || !addToProjectRoleId) return;
    setAddingToProject(true);
    setAddToProjectError(null);
    try {
      const memberId = selectedUser.raw?.id ?? null;
      const memberPayload = memberId != null
        ? { id: memberId }
        : { identifier: String(selectedUser.raw?.identifier ?? '') };
      await postAddMemberToProject(selectedProjectId, { member: memberPayload, projectRole: { id: addToProjectRoleId } }, token);
      setShowAddToProject(false);
      setSubmitError(null);
      await handleSubmit();
    } catch (e) {
      setAddToProjectError(e.message || String(e));
    } finally {
      setAddingToProject(false);
    }
  }

  if (!open) return null;

  return (
    <Modal onClose={onClose}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: '#e3f2fd' }}>Add Scheduled Resource</h3>
        <button style={S.btn} onClick={onClose} disabled={submitting}>Close</button>
      </div>

      {success ? (
        <div>
          <div style={{ color: '#66bb6a', fontSize: 14, marginBottom: 16 }}>
            ✓ Resource scheduled successfully.
          </div>
          <button style={S.primaryBtn} onClick={onClose}>Done</button>
        </div>
      ) : (
        <div>
          {/* Preset ticket display or project search + ticket dropdown */}
          {presetTicket ? (
            <div style={{ ...S.row, padding: '8px 10px', background: '#263238', borderRadius: 6, border: '1px solid #455a64' }}>
              <div style={{ fontWeight: 600, color: '#90caf9', fontSize: 13 }}>#{presetTicket.ticketNumber} — {presetTicket.ticketSummary}</div>
              <div style={{ fontSize: 11, color: '#78909c', marginTop: 2 }}>Project: {presetTicket.projectName || presetTicket.projectId}</div>
            </div>
          ) : (
            <>
              {/* Project # */}
              <div style={S.row}>
                <label style={S.label}>Project #</label>
                <div style={S.row2}>
                  <input
                    style={{ ...S.input, flex: 1 }}
                    placeholder="Numeric project ID, e.g. 12345"
                    value={projectInput}
                    onChange={e => setProjectInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleProjectSearch(); } }}
                  />
                  <button style={S.btn} onClick={handleProjectSearch} disabled={loadingTickets}>
                    {loadingTickets ? 'Loading…' : 'Search'}
                  </button>
                </div>
                {ticketError && <div style={S.error}>{ticketError}</div>}
              </div>

              {/* Ticket */}
              {projectTickets.length > 0 && (
                <div style={S.row}>
                  <label style={S.label}>Ticket</label>
                  <SearchableSelect
                    items={ticketOptions}
                    value={selectedTicketId}
                    onChange={(val) => setSelectedTicketId(val ?? null)}
                    placeholder="Select ticket…"
                  />
                </div>
              )}
            </>
          )}

          {/* Resource */}
          <div style={S.row}>
            <label style={S.label}>Resource {loadingUsers ? '(loading…)' : ''}</label>
            <SearchableSelect
              items={userOptions}
              value={selectedUser?.value ?? null}
              onChange={(val) => setSelectedUser(userOptions.find(o => o.value === val) ?? null)}
              placeholder="Select resource…"
            />
          </div>

          {/* Hours */}
          <div style={S.row}>
            <label style={S.label}>Hours (optional)</label>
            <input
              type="number"
              min="0"
              step="0.5"
              style={S.input}
              placeholder="e.g. 8"
              value={hours}
              onChange={e => setHours(e.target.value)}
            />
          </div>

          {/* Dates */}
          <div style={S.row}>
            <label style={S.label}>Start Date</label>
            <input type="date" style={S.input} value={dateStart} onChange={e => setDateStart(e.target.value)} />
          </div>

          {submitError && <div style={S.error}>{submitError}</div>}

          {showAddToProject && (
            <div style={{ marginTop: 10, padding: '10px 12px', background: '#263238', borderRadius: 6, border: '1px solid #455a64' }}>
              <div style={{ fontSize: 12, color: '#90caf9', fontWeight: 600, marginBottom: 6 }}>Add Member to Project</div>
              <div style={{ fontSize: 12, color: '#b0bec5', marginBottom: 8 }}>
                Select a project role to add <strong>{selectedUser?.label}</strong> to project <strong>{selectedProjectId}</strong>, then retry scheduling.
              </div>
              {loadingRoles ? (
                <div style={{ fontSize: 12, color: '#78909c' }}>Loading roles…</div>
              ) : (
                <select
                  value={addToProjectRoleId ?? ''}
                  onChange={e => setAddToProjectRoleId(e.target.value)}
                  style={{ ...S.input, marginBottom: 8 }}
                >
                  {projectRoles.map(r => {
                    const rid = r?.id ?? r?.identifier;
                    return <option key={rid} value={rid}>{r?.name || r?.title || `Role ${rid}`}</option>;
                  })}
                </select>
              )}
              {addToProjectError && <div style={S.error}>{addToProjectError}</div>}
              <button
                style={{ ...S.primaryBtn, fontSize: 12, padding: '6px 14px', opacity: addingToProject ? 0.7 : 1 }}
                onClick={handleAddToProject}
                disabled={addingToProject || !addToProjectRoleId || loadingRoles}
              >
                {addingToProject ? 'Adding…' : 'Add to Project & Retry'}
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button
              style={{ ...S.primaryBtn, opacity: submitting ? 0.7 : 1 }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Schedule'}
            </button>
            <button style={S.btn} onClick={onClose} disabled={submitting}>Cancel</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
